"use client";

import { TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, LABEL } from "@/components/tool-kit/styles";
import type { Strings } from "@/components/tools/real-gdp-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { Known } from "@/lib/tool-kit/econ-macro/gdp";

export interface Row {
  id: number;
  label: string;
  first: string;
  second: string;
  population: string;
}

const MAX_ROWS = 8;

function fieldNames(known: Known, strings: Strings): [string, string] {
  if (known === "nominal-deflator") {
    return [strings.nominalField, strings.deflatorField];
  }
  if (known === "real-deflator") {
    return [strings.realField, strings.deflatorField];
  }
  return [strings.nominalField, strings.realField];
}

/** One card per period: a name, the two known quantities and optional population. */
export function GdpForm({
  known,
  rows,
  setRows,
  nextId,
  setNextId,
  strings,
}: {
  known: Known;
  rows: Row[];
  setRows: (update: (old: Row[]) => Row[]) => void;
  nextId: number;
  setNextId: (id: number) => void;
  strings: Strings;
}) {
  const [firstName, secondName] = fieldNames(known, strings);
  const edit = (id: number, key: keyof Row) => (value: string) =>
    setRows((old) =>
      old.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  const add = () => {
    setRows((old) => [
      ...old,
      { id: nextId, label: "", first: "", second: "", population: "" },
    ]);
    setNextId(nextId + 1);
  };
  return (
    <div className="space-y-4">
      {rows.map((row, index) => (
        <fieldset
          className="space-y-3 rounded-lg border border-border p-4"
          key={row.id}
        >
          <legend className={`${LABEL} px-1`}>
            {t(strings.periodHeading, { n: String(index + 1) })}
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`gdp-label-${row.id}`}
              label={strings.periodName}
              onChange={edit(row.id, "label")}
              placeholder={t(strings.periodDefault, { n: String(index + 1) })}
              value={row.label}
            />
            <TextField
              id={`gdp-pop-${row.id}`}
              label={strings.populationField}
              numeric
              onChange={edit(row.id, "population")}
              value={row.population}
            />
            <TextField
              id={`gdp-first-${row.id}`}
              label={firstName}
              numeric
              onChange={edit(row.id, "first")}
              value={row.first}
            />
            <TextField
              id={`gdp-second-${row.id}`}
              label={secondName}
              numeric
              onChange={edit(row.id, "second")}
              value={row.second}
            />
          </div>
          {rows.length > 1 ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setRows((old) => old.filter((other) => other.id !== row.id))
              }
              type="button"
            >
              {t(strings.removePeriod, { n: String(index + 1) })}
            </button>
          ) : null}
        </fieldset>
      ))}
      {rows.length < MAX_ROWS ? (
        <button className={BUTTON_SECONDARY} onClick={add} type="button">
          {strings.addPeriod}
        </button>
      ) : null}
    </div>
  );
}
